import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http, HttpResponse, delay } from 'msw';

import { api } from 'lib/api-client';
import { MutationConfig } from 'lib/react-query';
import { env } from 'config/env';
import { CurrentSubscription } from 'types/api';
import { getCurrentSubscriptionQueryOptions } from './get-current-subscription';
import { getUserQueryOptions } from 'lib/auth';

export type AcceptCurrentSubscriptionInput = {
    current_subscription_id: string;
};

export type AcceptCurrentSubscriptionResponse = {
    current_subscription: CurrentSubscription;
};

export const acceptCurrentSubscription = async ({
    current_subscription_id,
}: AcceptCurrentSubscriptionInput): Promise<AcceptCurrentSubscriptionResponse> => {
    return await api.post(
        `/app/api/v1/current_subscription/${current_subscription_id}/accept`,
    );
};

type UseAcceptCurrentSubscriptionOptions = {
    mutationConfig?: MutationConfig<typeof acceptCurrentSubscription>;
};

export const useAcceptCurrentSubscription = ({
    mutationConfig,
}: UseAcceptCurrentSubscriptionOptions = {}) => {
    const queryClient = useQueryClient();

    const { onSuccess, ...restConfig } = mutationConfig || {};
    return useMutation({
        ...mutationConfig,
        mutationFn: acceptCurrentSubscription,
        onSuccess: (data, variables, context) => {
            queryClient.setQueryData(
                getCurrentSubscriptionQueryOptions().queryKey,
                data.current_subscription,
            );
            queryClient.invalidateQueries(getUserQueryOptions());
            onSuccess?.(data, variables, context);
        },
        ...restConfig,
    });
};

export const acceptCurrentSubscriptionMSWHandler = (
    status = 200,
    currentSubscription: CurrentSubscription,
    duration?: number,
) => {
    return http.post(
        `${env.API_URL}/app/api/v1/current_subscription/:current_subscription_id/accept`,
        async () => {
            await delay(duration);
            if (status >= 400) {
                return new HttpResponse(null, { status });
            }

            const response: AcceptCurrentSubscriptionResponse = {
                current_subscription: currentSubscription,
            };
            return HttpResponse.json(response, { status });
        },
    );
};
